<!-- src/views/employee/Dashboard.vue - Employee Dashboard -->
<template>
  <div class="ep-page">

    <!-- Loading State -->
    <div v-if="isLoadingDashboard" class="flex items-center justify-center py-24">
      <div class="text-center">
        <div class="ep-spinner mb-4"></div>
        <p class="text-sm text-slate-500">{{ t('empDashboard.loading') }}</p>
      </div>
    </div>

    <template v-else>
      <!-- ── Header ─────────────────────────────────────────── -->
      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 class="ep-title">Welcome back, {{ authStore.userName }} 👋</h1>
          <p class="ep-subtitle">{{ currentDate }}</p>
        </div>
        <div class="flex items-center gap-2">
          <span :class="['ep-badge', isOnline ? 'ep-badge-success' : 'ep-badge-danger']">
            <component :is="isOnline ? Wifi : WifiOff" class="w-3 h-3" />
            {{ isOnline ? t('empDashboard.online') : t('empDashboard.offline') }}
          </span>
          <span v-if="attendanceStatus.isActive" class="ep-badge ep-badge-accent">
            <span class="ep-dot animate-pulse"></span>
            {{ t('empDashboard.working') }} · {{ liveWorkDuration }}
          </span>
        </div>
      </div>

      <!-- ── Android app announcement ───────────────────────── -->
      <div v-if="showAppBanner"
           class="relative overflow-hidden rounded-2xl px-4 sm:px-5 py-4 flex items-center gap-4"
           style="background: linear-gradient(135deg, rgba(99,102,241,0.16) 0%, rgba(124,58,237,0.1) 100%); border: 1px solid rgba(129,140,248,0.3)">
        <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
             style="background: rgba(99,102,241,0.25); border: 1px solid rgba(129,140,248,0.4)">
          <Smartphone class="w-5 h-5 text-indigo-200" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-slate-100">{{ t('empDashboard.appBannerTitle') }}</p>
          <p class="text-xs text-indigo-200/70 mt-0.5 hidden sm:block">{{ t('empDashboard.appBannerDesc') }}</p>
        </div>
        <router-link to="/employee/get-the-app" class="ep-btn ep-btn-primary ep-btn-sm shrink-0">
          <Download class="w-4 h-4" />
          <span class="hidden sm:inline">{{ t('empDashboard.getApp') }}</span>
          <span class="sm:hidden">{{ t('empDashboard.getIt') }}</span>
        </router-link>
        <button @click="dismissAppBanner"
                class="p-1.5 rounded-lg text-indigo-200/60 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                title="Dismiss">
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- ── Hero: Clock status ─────────────────────────────── -->
      <div class="ep-card relative overflow-hidden p-5 sm:p-6">
        <!-- accent glow -->
        <div class="absolute -top-24 -right-24 w-64 h-64 rounded-full pointer-events-none"
             :style="{ background: attendanceStatus.isActive
               ? 'radial-gradient(circle, rgba(52,211,153,0.12) 0%, transparent 70%)'
               : 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)' }"></div>

        <div class="relative flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
          <!-- Status -->
          <div class="flex items-center gap-4 flex-1 min-w-0">
            <div :class="['ep-stat-icon', attendanceStatus.isActive ? 'ep-stat-emerald' : 'ep-stat-indigo']">
              <Clock class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <span :class="['w-2 h-2 rounded-full shrink-0', attendanceStatus.isActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600']"></span>
                <p class="text-sm font-semibold text-slate-200">
                  {{ attendanceStatus.isActive ? t('dashboard.clockedIn') : t('dashboard.clockedOut') }}
                </p>
              </div>
              <p v-if="attendanceStatus.isActive" class="text-xs text-slate-500 mt-0.5">
                {{ t('empDashboard.since') }} {{ formatTime(attendanceStatus.clockInTime) }} · {{ t('empDashboard.started') }} {{ getTimeAgo(attendanceStatus.clockInTime) }}
              </p>
              <p v-else-if="lastClockOut" class="text-xs text-slate-500 mt-0.5 truncate">
                {{ t('empDashboard.lastWorked') }} {{ formatDate(lastClockOut.date) }} · {{ lastClockOut.duration }}
              </p>
              <p v-else class="text-xs text-slate-500 mt-0.5">{{ t('empDashboard.readyToStart') }}</p>
            </div>
          </div>

          <!-- Live duration -->
          <div v-if="attendanceStatus.isActive" class="flex items-center gap-3">
            <Activity class="w-4 h-4 text-emerald-400" />
            <p class="text-3xl font-bold tabular-nums tracking-tight text-emerald-300">{{ liveWorkDuration }}</p>
          </div>

          <!-- Quick stats + CTA -->
          <div class="flex items-center gap-5 lg:gap-6">
            <div class="text-center">
              <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">{{ t('empDashboard.today') }}</p>
              <p class="text-sm font-semibold text-slate-200 mt-0.5">{{ todayStats.hours }}</p>
            </div>
            <div class="w-px h-8 bg-white/10"></div>
            <div class="text-center">
              <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">{{ t('empDashboard.thisWeek') }}</p>
              <p class="text-sm font-semibold text-slate-200 mt-0.5">{{ weekStats.hours }}</p>
            </div>
            <router-link
              to="/employee/attendance"
              :class="['ep-btn', attendanceStatus.isActive ? 'ep-btn-danger' : 'ep-btn-primary']"
            >
              <Clock class="w-4 h-4" />
              {{ attendanceStatus.isActive ? t('empDashboard.clockOut') : t('empDashboard.clockIn') }}
            </router-link>
          </div>
        </div>
      </div>

      <!-- ── Stats grid ─────────────────────────────────────── -->
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <!-- Today's Summary -->
        <div class="ep-card ep-card-hover p-5">
          <div class="flex items-center gap-3 mb-4">
            <div class="ep-stat-icon ep-stat-emerald"><Calendar class="w-5 h-5" /></div>
            <div>
              <p class="text-sm font-semibold text-slate-200">{{ t('empDashboard.today') }}</p>
              <p class="text-xs text-slate-500">{{ t('empDashboard.dailySummary') }}</p>
            </div>
          </div>
          <div class="space-y-2.5">
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.hoursWorked') }}</span>
              <span class="font-semibold text-slate-200">{{ todaySummary.hoursWorked }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.breaks') }}</span>
              <span class="font-semibold text-slate-200">{{ todaySummary.breakCount }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.location') }}</span>
              <span class="flex items-center gap-1 text-xs text-slate-400 truncate max-w-[10rem]">
                <MapPin class="w-3 h-3 shrink-0" />
                {{ todaySummary.location || 'N/A' }}
              </span>
            </div>
          </div>
          <router-link to="/employee/history"
                       class="mt-4 flex items-center justify-between text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors group">
            {{ t('empDashboard.viewHistory') }}
            <ChevronRight class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </router-link>
        </div>

        <!-- Leave Balance -->
        <div class="ep-card ep-card-hover p-5">
          <div class="flex items-center gap-3 mb-4">
            <div class="ep-stat-icon ep-stat-amber"><FileText class="w-5 h-5" /></div>
            <div>
              <p class="text-sm font-semibold text-slate-200">{{ t('empDashboard.leaveBalance') }}</p>
              <p class="text-xs text-slate-500">{{ t('empDashboard.availableDays') }}</p>
            </div>
          </div>
          <div class="space-y-2.5">
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.annualLeave') }}</span>
              <span class="font-semibold text-slate-200">{{ leaveBalance.annual }} {{ t('empDashboard.days') }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.sickLeave') }}</span>
              <span class="font-semibold text-slate-200">{{ leaveBalance.sick }} {{ t('empDashboard.days') }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.personal') }}</span>
              <span class="font-semibold text-slate-200">{{ leaveBalance.personal }} {{ t('empDashboard.days') }}</span>
            </div>
          </div>
          <router-link to="/employee/leave-requests"
                       class="mt-4 flex items-center justify-between text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors group">
            {{ t('empDashboard.requestLeave') }}
            <ChevronRight class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </router-link>
        </div>

        <!-- This Month -->
        <div class="ep-card ep-card-hover p-5 sm:col-span-2 xl:col-span-1">
          <div class="flex items-center gap-3 mb-4">
            <div class="ep-stat-icon ep-stat-violet"><BarChart3 class="w-5 h-5" /></div>
            <div>
              <p class="text-sm font-semibold text-slate-200">{{ t('empDashboard.thisMonth') }}</p>
              <p class="text-xs text-slate-500">{{ t('empDashboard.workStatistics') }}</p>
            </div>
          </div>
          <div class="space-y-2.5">
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.totalHours') }}</span>
              <span class="font-semibold text-slate-200">{{ monthlyStats.totalHours }}h</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.daysWorked') }}</span>
              <span class="font-semibold text-slate-200">{{ monthlyStats.daysWorked }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('empDashboard.avgPerDay') }}</span>
              <span class="font-semibold text-slate-200">{{ monthlyStats.averageHours }}h</span>
            </div>
          </div>
          <div class="mt-4">
            <div class="ep-progress">
              <div class="ep-progress-bar" :style="{ width: `${monthlyStats.progressPercentage}%` }"></div>
            </div>
            <p class="text-[11px] text-slate-500 mt-1.5 text-right">{{ monthlyStats.progressPercentage }}{{ t('empDashboard.ofMonthlyTarget') }}</p>
          </div>
        </div>
      </div>

      <!-- ── Activity row ───────────────────────────────────── -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Recent Attendance -->
        <div class="ep-card overflow-hidden">
          <div class="flex items-center justify-between px-5 py-4"
               style="border-bottom: 1px solid rgba(255,255,255,0.06)">
            <h3 class="ep-section-title"><Clock class="w-4 h-4 text-indigo-400" /> {{ t('empDashboard.recentAttendance') }}</h3>
            <router-link to="/employee/history" class="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
              {{ t('empDashboard.viewAll') }}
            </router-link>
          </div>

          <div class="p-4">
            <div v-if="isLoadingHistory" class="py-8"><div class="ep-spinner"></div></div>

            <div v-else-if="recentAttendance.length === 0" class="ep-empty">
              <Clock class="w-10 h-10" />
              <p class="text-sm text-slate-500">{{ t('empDashboard.noAttendanceYet') }}</p>
              <router-link to="/employee/attendance" class="text-xs font-medium text-indigo-400 hover:text-indigo-300">
                {{ t('empDashboard.clockInNow') }}
              </router-link>
            </div>

            <div v-else class="space-y-2">
              <div v-for="record in recentAttendance" :key="record.id"
                   class="flex items-center justify-between px-3.5 py-3 rounded-xl transition-colors hover:bg-white/[0.03]"
                   style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04)">
                <div class="flex items-center gap-3 min-w-0">
                  <span :class="['w-2 h-2 rounded-full shrink-0', getStatusColor(record.status)]"></span>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-slate-200">{{ formatDate(record.date) }}</p>
                    <p class="text-xs text-slate-500">{{ record.clockInTime }} – {{ record.clockOutTime || t('empDashboard.inProgress') }}</p>
                  </div>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-sm font-semibold tabular-nums text-slate-200">
                    {{ record.duration || calculateDuration(record.clockInTime) }}
                  </p>
                  <p class="text-[11px] text-slate-500 capitalize">{{ record.status }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notifications -->
        <div class="ep-card overflow-hidden">
          <div class="flex items-center justify-between px-5 py-4"
               style="border-bottom: 1px solid rgba(255,255,255,0.06)">
            <h3 class="ep-section-title"><Bell class="w-4 h-4 text-indigo-400" /> {{ t('notifications.title') }}</h3>
            <button @click="markAllNotificationsRead"
                    class="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
              {{ t('notifications.markAllRead') }}
            </button>
          </div>

          <div class="p-4">
            <div v-if="isLoadingNotifications" class="py-8"><div class="ep-spinner"></div></div>

            <div v-else-if="notifications.length === 0" class="ep-empty">
              <Bell class="w-10 h-10" />
              <p class="text-sm text-slate-500">{{ t('empDashboard.allCaughtUp') }}</p>
            </div>

            <div v-else class="space-y-2">
              <div v-for="notification in notifications" :key="notification.id"
                   @click="markNotificationRead(notification.id)"
                   :class="['px-3.5 py-3 rounded-xl cursor-pointer transition-colors',
                            notification.isRead ? 'hover:bg-white/[0.03]' : 'hover:bg-indigo-500/10']"
                   :style="notification.isRead
                     ? 'background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04)'
                     : 'background: rgba(99,102,241,0.07); border: 1px solid rgba(99,102,241,0.2)'">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p :class="['text-sm text-slate-200 truncate', notification.isRead ? '' : 'font-semibold']">
                      {{ notification.title }}
                    </p>
                    <p class="text-xs text-slate-500 mt-0.5 line-clamp-2">{{ notification.message }}</p>
                    <p class="text-[11px] text-slate-600 mt-1">{{ formatRelativeTime(notification.createdAt) }}</p>
                  </div>
                  <span v-if="!notification.isRead" class="w-2 h-2 bg-indigo-400 rounded-full mt-1.5 shrink-0"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Weather (optional) ─────────────────────────────── -->
      <div v-if="weatherData" class="ep-card p-5 relative overflow-hidden">
        <div class="absolute inset-0 pointer-events-none"
             style="background: linear-gradient(120deg, rgba(56,189,248,0.08) 0%, transparent 60%)"></div>
        <div class="relative flex items-center justify-between">
          <div>
            <p class="text-sm font-semibold text-slate-200">Current Weather</p>
            <p class="text-xs text-slate-500 mt-0.5">{{ weatherData.location }}</p>
          </div>
          <div class="text-right">
            <p class="text-3xl font-bold text-sky-300">{{ weatherData.temperature }}°C</p>
            <p class="text-xs text-slate-500">{{ weatherData.condition }}</p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useAttendanceStore } from '@/stores/attendance';

const { t } = useI18n();
import {
  Clock,
  Calendar,
  FileText,
  BarChart3,
  Bell,
  MapPin,
  ChevronRight,
  Activity,
  Wifi,
  WifiOff,
  Smartphone,
  Download,
  X,
} from 'lucide-vue-next';

import { dashboardService } from '@/services/dashboardService';

// Composables
const router = useRouter();
const authStore = useAuthStore();
const attendanceStore = useAttendanceStore();

// Reactive state
const isOnline = ref(navigator.onLine);

// Android app announcement banner (dismiss persists per browser)
const APP_BANNER_KEY = 'exotrack-app-banner-dismissed';
const showAppBanner = ref(localStorage.getItem(APP_BANNER_KEY) !== '1');
const dismissAppBanner = () => {
  showAppBanner.value = false;
  localStorage.setItem(APP_BANNER_KEY, '1');
};
const isLoadingHistory = ref(false);
const isLoadingNotifications = ref(false);
const isLoadingDashboard = ref(true);

// Dashboard data - initialized with loading states
const todaySummary = ref({
  hoursWorked: '0h 0m',
  breakCount: 0,
  location: null,
  isActive: false,
  clockInTime: null,
  clockOutTime: null
});

const todayStats = ref({
  hours: '0h 0m',
});

const weekStats = ref({
  hours: '0h 0m',
});

const leaveBalance = ref({
  annual: 0,
  sick: 0,
  personal: 0,
});

const monthlyStats = ref({
  totalHours: 0,
  daysWorked: 0,
  averageHours: 0,
  progressPercentage: 0,
});

const lastClockOut = ref(null);

const recentAttendance = ref([]);

const notifications = ref([]);

const weatherData = ref(null);

// Computed
const currentDate = computed(() => {
  return attendanceStore.currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
});

// Use attendance store for all timing data
const liveWorkDuration = computed(() => {
  return attendanceStore.liveWorkDuration;
});

const currentWorkDuration = computed(() => {
  return attendanceStore.currentWorkDuration;
});

const attendanceStatus = computed(() => ({
  isActive: attendanceStore.isActive,
  clockInTime: attendanceStore.clockInTime,
  clockOutTime: attendanceStore.clockOutTime,
}));

// Methods
const loadDashboardData = async () => {
  try {
    isLoadingDashboard.value = true;
    console.log('🔄 Loading dashboard data...');
    
    const response = await dashboardService.getDashboardData();
    if (response.success) {
      const data = response.data;
      
      // Update user info in auth store if employee data is available
      if (data.employee) {
        // Sync employee data with auth store
        const currentUser = authStore.user || {};
        const updatedUser = {
          ...currentUser,
          id: data.employee.id,
          name: data.employee.name,
          email: data.employee.email,
          position: data.employee.position,
          join_date: data.employee.joinDate
        };
        
        // Update the auth store with complete user info
        authStore.user = updatedUser;
        console.log('👤 User data synchronized:', {
          name: updatedUser.name,
          email: updatedUser.email,
          position: updatedUser.position
        });
      }
      
      // Update all dashboard data
      todaySummary.value = data.todaySummary || todaySummary.value;
      todayStats.value = data.todayStats || todayStats.value;
      weekStats.value = data.weekStats || weekStats.value;
      leaveBalance.value = data.leaveBalance || leaveBalance.value;
      monthlyStats.value = data.monthlyStats || monthlyStats.value;
      recentAttendance.value = data.recentAttendance || [];
      notifications.value = data.notifications || [];
      
      // Update attendance status in attendance store if needed
      if (data.attendanceStatus || data.todaySummary) {
        // Create today's attendance data structure for the store
        const todayAttendanceData = {
          clock_in_time: data.todaySummary?.clockInTime || data.attendanceStatus?.clockInTime,
          clock_out_time: data.todaySummary?.clockOutTime || data.attendanceStatus?.clockOutTime,
          is_active: data.todaySummary?.isActive || data.attendanceStatus?.isActive || false,
          duration: data.todaySummary?.hoursWorked || '0h 0m'
        };
        
        // Update attendance store with today's data
        attendanceStore.todayAttendance = todayAttendanceData;
        
        console.log('📊 Attendance status synchronized:', {
          clockInTime: todayAttendanceData.clock_in_time,
          clockOutTime: todayAttendanceData.clock_out_time,
          isActive: todayAttendanceData.is_active,
          duration: todayAttendanceData.duration
        });
      }
      
      console.log('✅ Dashboard data loaded successfully');
    } else {
      console.error('❌ Failed to load dashboard data:', response.error);
    }
  } catch (error) {
    console.error('❌ Error loading dashboard data:', error);
  } finally {
    isLoadingDashboard.value = false;
  }
};

const loadAttendanceHistory = async () => {
  try {
    isLoadingHistory.value = true;
    const response = await dashboardService.getRecentAttendance(5);
    if (response.success && response.data) {
      recentAttendance.value = response.data.history || response.data || [];
    }
  } catch (error) {
    console.error('Failed to load attendance history:', error);
    // Fallback to attendance store
    try {
      await attendanceStore.fetchAttendanceHistory({ limit: 5 });
      recentAttendance.value = attendanceStore.attendanceHistory || [];
    } catch (fallbackError) {
      console.error('Fallback attendance history failed:', fallbackError);
    }
  } finally {
    isLoadingHistory.value = false;
  }
};

const loadNotifications = async () => {
  try {
    isLoadingNotifications.value = true;
    const response = await dashboardService.getNotifications(10);
    if (response.success && response.data) {
      notifications.value = response.data.notifications || [];
    }
  } catch (error) {
    console.error('Failed to load notifications:', error);
    // Keep existing mock notifications if API fails
  } finally {
    isLoadingNotifications.value = false;
  }
};

const refreshDashboard = async () => {
  console.log('🔄 Refreshing dashboard...');
  await Promise.all([
    loadDashboardData(),
    loadNotifications(),
  ]);
};

const markNotificationRead = async notificationId => {
  try {
    await dashboardService.markNotificationRead(notificationId);
    const notification = notifications.value.find(n => n.id === notificationId);
    if (notification) {
      notification.isRead = true;
    }
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
    // Still update locally for better UX
    const notification = notifications.value.find(n => n.id === notificationId);
    if (notification) {
      notification.isRead = true;
    }
  }
};

const markAllNotificationsRead = async () => {
  try {
    await dashboardService.markAllNotificationsRead();
    notifications.value.forEach(notification => {
      notification.isRead = true;
    });
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
    // Still update locally for better UX
    notifications.value.forEach(notification => {
      notification.isRead = true;
    });
  }
};

const formatTime = timeString => {
  if (!timeString) return 'N/A';
  return timeString;
};

const formatDate = dateString => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatRelativeTime = dateString => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

const getTimeAgo = timeString => {
  if (!timeString) return '';

  const clockInDate = new Date(`${new Date().toDateString()} ${timeString}`);
  const now = new Date();
  const diff = now - clockInDate;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m ago`;
  }
  return `${minutes}m ago`;
};

const getStatusColor = status => {
  switch (status) {
    case 'approved':
      return 'bg-green-400';
    case 'pending':
      return 'bg-yellow-400';
    case 'rejected':
      return 'bg-red-400';
    default:
      return 'bg-gray-400';
  }
};

const calculateDuration = clockInTime => {
  if (!clockInTime || clockInTime === '--:--') return '0h 0m';

  try {
    const today = new Date().toDateString();
    const start = new Date(`${today} ${clockInTime}`);
    const now = attendanceStore.currentTime;
    
    // Validate dates
    if (isNaN(start.getTime()) || !now || isNaN(now.getTime())) {
      return '0h 0m';
    }
    
    const diff = now - start;
    
    // Ensure positive difference
    if (diff < 0) {
      return '0h 0m';
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    // Validate calculated values
    if (isNaN(hours) || isNaN(minutes)) {
      return '0h 0m';
    }

    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error('Error calculating duration:', error);
    return '0h 0m';
  }
};

const handleOnlineStatus = () => {
  isOnline.value = navigator.onLine;
};

// Lifecycle
onMounted(async () => {
  console.log('🚀 Dashboard mounting...');
  
  // Set up online/offline listeners
  window.addEventListener('online', handleOnlineStatus);
  window.addEventListener('offline', handleOnlineStatus);

  // Initialize attendance store (this starts the timer)
  await attendanceStore.initialize();

  // Load comprehensive dashboard data from API
  await loadDashboardData();

  // Set up periodic refresh (every 5 minutes)
  const refreshInterval = setInterval(async () => {
    if (isOnline.value) {
      console.log('🔄 Auto-refreshing dashboard data...');
      await loadDashboardData();
    }
  }, 5 * 60 * 1000); // 5 minutes

  // Cleanup function
  onUnmounted(() => {
    window.removeEventListener('online', handleOnlineStatus);
    window.removeEventListener('offline', handleOnlineStatus);
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    console.log('🧹 Dashboard cleanup completed');
  });
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
